import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

const MESES_ES = ['','Enero','Febrero','Marzo','Abril','Mayo','Junio',
                  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const CAMPOS_FIJOS = [
  { key: 'sueldos',   label: 'Sueldos',          icon: '👥' },
  { key: 'iess',      label: 'IESS',              icon: '🏥' },
  { key: 'sri',       label: 'SRI',               icon: '🏛️' },
  { key: 'alquiler',  label: 'Alquiler',          icon: '🏢' },
  { key: 'prestamos', label: 'Préstamos',         icon: '🏦' },
  { key: 'tarjeta1',  label: 'Tarjeta crédito 1', icon: '💳' },
  { key: 'tarjeta2',  label: 'Tarjeta crédito 2', icon: '💳' },
  { key: 'luz',       label: 'Luz',               icon: '💡' },
  { key: 'agua',      label: 'Agua',              icon: '💧' },
  { key: 'internet',  label: 'Internet',          icon: '📡' },
];

function fmt(n) {
  return (n || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function colorUtilidad(val) {
  if (val > 0)  return '#4caf50';
  if (val === 0) return '#aaa';
  return '#e63946';
}

export default function CierreMensual() {
  const hoy   = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes,  setMes]  = useState(hoy.getMonth() + 1);

  const [datos,     setDatos]     = useState(null);
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [exito,     setExito]     = useState('');
  const [error,     setError]     = useState('');

  // Inputs controlados
  const [gastos,  setGastos]  = useState({});
  const [varios,  setVarios]  = useState([]);  // [{nombre, valor}]
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoValor,  setNuevoValor]  = useState('');

  const cargar = useCallback(async (a, m) => {
    setCargando(true);
    setExito('');
    setError('');
    try {
      const data = await api.get(`/finanzas/cierre?anio=${a}&mes=${m}`);
      if (data.error) { setDatos(null); return; }
      setDatos(data);
      // Inicializar inputs con valores guardados
      const g = data.gastos || {};
      const init = {};
      CAMPOS_FIJOS.forEach(c => { init[c.key] = g[c.key] > 0 ? String(g[c.key]) : ''; });
      setGastos(init);
      setVarios(g.varios || []);
    } catch (e) { setDatos(null); }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(anio, mes); }, [anio, mes, cargar]);

  // Calcular totales en tiempo real
  const totalFijos  = CAMPOS_FIJOS.reduce((sum, c) => sum + (parseFloat(gastos[c.key]) || 0), 0);
  const totalVarios = varios.reduce((sum, v) => sum + (parseFloat(v.valor) || 0), 0);
  const totalGastos = totalFijos + totalVarios;
  const margenBruto = datos?.margen_bruto || 0;
  const utilidadNeta = margenBruto - totalGastos;
  const revNeto      = datos?.revenue?.neto || 0;
  const utilidadPct  = revNeto > 0 ? (utilidadNeta / revNeto * 100) : 0;

  const mesAnterior = () => {
    if (mes === 1) { setMes(12); setAnio(a => a - 1); }
    else setMes(m => m - 1);
  };
  const mesSiguiente = () => {
    const esActual = mes === hoy.getMonth() + 1 && anio === hoy.getFullYear();
    if (esActual) return;
    if (mes === 12) { setMes(1); setAnio(a => a + 1); }
    else setMes(m => m + 1);
  };

  const handleGasto = (key, val) => {
    setGastos(prev => ({ ...prev, [key]: val }));
  };

  const agregarVario = () => {
    if (!nuevoNombre.trim() || !nuevoValor) return;
    setVarios(prev => [...prev, { nombre: nuevoNombre.trim(), valor: parseFloat(nuevoValor) || 0 }]);
    setNuevoNombre('');
    setNuevoValor('');
  };

  const eliminarVario = (i) => setVarios(prev => prev.filter((_, idx) => idx !== i));

  const guardar = async () => {
    setGuardando(true);
    setExito('');
    setError('');
    try {
      const payload = { anio, mes, ...Object.fromEntries(
        CAMPOS_FIJOS.map(c => [c.key, parseFloat(gastos[c.key]) || 0])
      ), varios };
      const res = await api.post('/finanzas/cierre', payload);
      if (res.ok) {
        setExito('✅ Cierre guardado');
        cargar(anio, mes);
      } else {
        setError(res.error || 'Error al guardar');
      }
    } catch (e) { setError('Error de conexión'); }
    finally { setGuardando(false); }
  };

  const esActual = mes === hoy.getMonth() + 1 && anio === hoy.getFullYear();

  return (
    <div>
      {/* ── Selector de mes ── */}
      <div style={s.mesRow}>
        <button style={s.flechaMes} onClick={mesAnterior}>‹</button>
        <span style={s.mesLabel}>{MESES_ES[mes]} {anio}{esActual ? ' • Actual' : ''}</span>
        <button style={{ ...s.flechaMes, ...(esActual ? s.flechaOff : {}) }} onClick={mesSiguiente}>›</button>
        <button style={s.btnRefresh} onClick={() => cargar(anio, mes)}>🔄</button>
      </div>

      {cargando ? (
        <p style={s.cargando}>Cargando P&L de {MESES_ES[mes]}...</p>
      ) : !datos ? (
        <p style={{ color: '#e63946' }}>No se pudo cargar el cierre</p>
      ) : (
        <div style={s.grid}>

          {/* ═══ COLUMNA IZQUIERDA — Inputs de gastos ═══ */}
          <div style={s.colLeft}>
            <div style={s.cardHeader}>
              <span style={s.cardTitulo}>📝 Gastos operacionales</span>
              <span style={s.cardSub}>Ingresa los valores del mes</span>
            </div>

            {/* Campos fijos */}
            <div style={s.inputsGrid}>
              {CAMPOS_FIJOS.map(c => (
                <div key={c.key} style={s.inputGroup}>
                  <label style={s.inputLabel}>{c.icon} {c.label}</label>
                  <div style={s.inputWrap}>
                    <span style={s.inputPrefix}>$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={s.input}
                      value={gastos[c.key] || ''}
                      onChange={e => handleGasto(c.key, e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Separador Varios */}
            <div style={s.variosHeader}>
              <span style={s.cardTitulo}>➕ Varios</span>
            </div>

            {/* Lista de varios existentes */}
            {varios.map((v, i) => (
              <div key={i} style={s.varioRow}>
                <span style={s.varioNombre}>{v.nombre}</span>
                <span style={s.varioValor}>${fmt(v.valor)}</span>
                <button style={s.btnElim} onClick={() => eliminarVario(i)}>✕</button>
              </div>
            ))}

            {/* Agregar nuevo vario */}
            <div style={s.nuevoVarioRow}>
              <input
                style={{ ...s.input, flex: 2, borderRadius: 6 }}
                placeholder="Nombre del gasto"
                value={nuevoNombre}
                onChange={e => setNuevoNombre(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && agregarVario()}
              />
              <div style={{ ...s.inputWrap, flex: 1 }}>
                <span style={s.inputPrefix}>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  style={s.input}
                  placeholder="0.00"
                  value={nuevoValor}
                  onChange={e => setNuevoValor(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && agregarVario()}
                />
              </div>
              <button style={s.btnAgregar} onClick={agregarVario}>+ Agregar</button>
            </div>

            {/* Botón guardar */}
            <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                style={{ ...s.btnGuardar, opacity: guardando ? 0.6 : 1 }}
                onClick={guardar}
                disabled={guardando}>
                {guardando ? 'Guardando...' : '💾 Guardar cierre'}
              </button>
              {exito && <span style={{ color: '#4caf50', fontSize: 13 }}>{exito}</span>}
              {error && <span style={{ color: '#e63946', fontSize: 13 }}>{error}</span>}
            </div>
          </div>

          {/* ═══ COLUMNA DERECHA — P&L ═══ */}
          <div style={s.colRight}>

            {/* ── Estado de resultados ── */}
            <div style={s.plCard}>
              <div style={s.plTitulo}>📊 Estado de resultados — {MESES_ES[mes]} {anio}</div>

              {/* INGRESOS */}
              <div style={s.plSeccion}>INGRESOS</div>
              <PlRow label="Revenue bruto" valor={datos.revenue.bruto} color="#aaa" />
              {datos.revenue.devoluciones > 0 && (
                <PlRow
                  label={`Devoluciones (${datos.revenue.total_nc} NC)`}
                  valor={-datos.revenue.devoluciones}
                  color="#e63946"
                />
              )}
              <PlRow label="Revenue neto" valor={datos.revenue.neto} color="#4cc9f0" bold />
              <div style={s.plDivider} />

              {/* COSTO MERCADERÍA */}
              <div style={s.plSeccion}>COSTO DE MERCADERÍA</div>
              <PlRow label="Costo productos vendidos" valor={-datos.costo_mercaderia} color="#e63946" />
              <PlRow
                label={`Margen bruto (${datos.margen_pct}%)`}
                valor={datos.margen_bruto}
                color={datos.margen_bruto >= 0 ? '#4caf50' : '#e63946'}
                bold
              />
              <div style={s.plDivider} />

              {/* GASTOS OPERACIONALES */}
              <div style={s.plSeccion}>GASTOS OPERACIONALES</div>
              {CAMPOS_FIJOS.map(c => {
                const val = parseFloat(gastos[c.key]) || 0;
                if (!val) return null;
                return <PlRow key={c.key} label={c.label} valor={-val} color="#aaa" />;
              })}
              {varios.map((v, i) => (
                <PlRow key={i} label={v.nombre} valor={-(parseFloat(v.valor) || 0)} color="#aaa" />
              ))}
              {totalGastos > 0 && (
                <>
                  <div style={s.plDivider} />
                  <PlRow label="Total gastos" valor={-totalGastos} color="#e63946" bold />
                </>
              )}
              <div style={s.plDivider} />

              {/* UTILIDAD NETA */}
              <div style={{ ...s.plRow, background: utilidadNeta >= 0 ? '#1a3a2a' : '#3a1a1a', borderRadius: 8, padding: '12px 16px', marginTop: 4 }}>
                <span style={{ color: '#fff', fontWeight: 'bold', fontSize: 14 }}>
                  {utilidadNeta >= 0 ? '✅' : '❌'} Utilidad neta
                  <span style={{ color: '#555', fontWeight: 'normal', fontSize: 12, marginLeft: 8 }}>
                    ({utilidadPct.toFixed(1)}% sobre revenue)
                  </span>
                </span>
                <span style={{ color: colorUtilidad(utilidadNeta), fontWeight: 'bold', fontSize: 18 }}>
                  {utilidadNeta < 0 ? '-' : ''}${fmt(Math.abs(utilidadNeta))}
                </span>
              </div>
            </div>

            {/* ── Contraste bancario ── */}
            <div style={s.bancoCard}>
              <div style={s.plTitulo}>🏦 Contraste bancario</div>
              {!datos.banco.disponible ? (
                <p style={{ color: '#555', fontSize: 13, marginTop: 8 }}>
                  No hay estado de cuenta cargado para este mes.
                  Súbelo en el módulo Banco.
                </p>
              ) : (
                <>
                  {datos.banco.cuentas.map((b, i) => (
                    <div key={i} style={s.bancoCuenta}>
                      <div style={{ color: '#4cc9f0', fontSize: 12, fontWeight: 'bold', marginBottom: 8 }}>
                        {b.banco}
                      </div>
                      <div style={s.bancoRow}>
                        <span style={s.bancoLabel}>Saldo anterior</span>
                        <span style={s.bancoVal}>${fmt(b.saldo_anterior)}</span>
                      </div>
                      <div style={s.bancoRow}>
                        <span style={s.bancoLabel}>Créditos (entradas)</span>
                        <span style={{ ...s.bancoVal, color: '#4caf50' }}>+${fmt(b.total_creditos)}</span>
                      </div>
                      <div style={s.bancoRow}>
                        <span style={s.bancoLabel}>Débitos (salidas)</span>
                        <span style={{ ...s.bancoVal, color: '#e63946' }}>-${fmt(b.total_debitos)}</span>
                      </div>
                      <div style={{ ...s.bancoRow, borderTop: '1px solid #2d2d44', paddingTop: 8, marginTop: 4 }}>
                        <span style={{ ...s.bancoLabel, color: '#fff' }}>Saldo final</span>
                        <span style={{ ...s.bancoVal, color: '#fff', fontWeight: 'bold', fontSize: 15 }}>
                          ${fmt(b.saldo_final)}
                        </span>
                      </div>
                    </div>
                  ))}

                  {/* Resumen consolidado bancario */}
                  {(() => {
                    const totalCreditos = datos.banco.cuentas.reduce((s, b) => s + b.total_creditos, 0);
                    const totalDebitos  = datos.banco.cuentas.reduce((s, b) => s + b.total_debitos,  0);
                    const saldoNeto     = totalCreditos - totalDebitos;
                    return (
                      <div style={s.diffCard}>
                        <div style={s.bancoRow}>
                          <span style={s.bancoLabel}>Total entradas (créditos)</span>
                          <span style={{ ...s.bancoVal, color: '#4caf50' }}>+${fmt(totalCreditos)}</span>
                        </div>
                        <div style={s.bancoRow}>
                          <span style={s.bancoLabel}>Total salidas (débitos)</span>
                          <span style={{ ...s.bancoVal, color: '#e63946' }}>-${fmt(totalDebitos)}</span>
                        </div>
                        <div style={{ ...s.bancoRow, borderTop: '1px solid #2d2d44', paddingTop: 8, marginTop: 4 }}>
                          <span style={{ ...s.bancoLabel, color: '#fff' }}>Flujo neto del mes</span>
                          <span style={{
                            ...s.bancoVal, fontWeight: 'bold', fontSize: 15,
                            color: saldoNeto >= 0 ? '#4caf50' : '#e63946'
                          }}>
                            {saldoNeto >= 0 ? '+' : '-'}${fmt(Math.abs(saldoNeto))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Fila del P&L ──
function PlRow({ label, valor, color, bold }) {
  return (
    <div style={s.plRow}>
      <span style={{ color: bold ? '#fff' : '#aaa', fontWeight: bold ? 'bold' : 'normal', fontSize: 13 }}>
        {label}
      </span>
      <span style={{ color: color || '#fff', fontWeight: bold ? 'bold' : 'normal', fontSize: bold ? 14 : 13 }}>
        {valor < 0 ? '-' : ''}${fmt(Math.abs(valor))}
      </span>
    </div>
  );
}

const s = {
  mesRow:        { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, background: '#1e1e2e', borderRadius: 10, padding: '10px 20px', width: 'fit-content' },
  flechaMes:     { background: 'none', border: 'none', color: '#4cc9f0', fontSize: 24, cursor: 'pointer', fontWeight: 'bold', padding: '0 4px' },
  flechaOff:     { color: '#444', cursor: 'default' },
  mesLabel:      { color: '#fff', fontSize: 15, fontWeight: 'bold', minWidth: 160, textAlign: 'center' },
  btnRefresh:    { background: 'none', border: '1px solid #2d2d44', borderRadius: 6, color: '#555', padding: '4px 10px', cursor: 'pointer', fontSize: 13 },
  cargando:      { color: '#aaa', padding: 40, textAlign: 'center' },
  grid:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' },

  // Columna izquierda
  colLeft:       { background: '#1e1e2e', borderRadius: 12, padding: 24 },
  cardHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  cardTitulo:    { color: '#fff', fontWeight: 'bold', fontSize: 14 },
  cardSub:       { color: '#555', fontSize: 11 },
  inputsGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: 20 },
  inputGroup:    { display: 'flex', flexDirection: 'column', gap: 4 },
  inputLabel:    { color: '#888', fontSize: 11, fontWeight: 'bold' },
  inputWrap:     { display: 'flex', alignItems: 'center', background: '#0f0f1a', border: '1px solid #2d2d44', borderRadius: 6, overflow: 'hidden' },
  inputPrefix:   { color: '#555', padding: '0 8px', fontSize: 13, borderRight: '1px solid #2d2d44', lineHeight: '34px' },
  input:         { background: 'transparent', border: 'none', color: '#fff', padding: '8px 10px', fontSize: 13, outline: 'none', width: '100%' },
  variosHeader:  { borderTop: '1px solid #2d2d44', paddingTop: 16, marginBottom: 12 },
  varioRow:      { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #1a1a2e' },
  varioNombre:   { flex: 1, color: '#aaa', fontSize: 13 },
  varioValor:    { color: '#4cc9f0', fontSize: 13, fontWeight: 'bold', minWidth: 80, textAlign: 'right' },
  btnElim:       { background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 14, padding: '0 4px' },
  nuevoVarioRow: { display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' },
  btnAgregar:    { background: '#1a2a3e', border: '1px solid #4cc9f0', borderRadius: 6, color: '#4cc9f0', padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 'bold', whiteSpace: 'nowrap' },
  btnGuardar:    { background: '#1a3a2a', border: '1px solid #4caf50', borderRadius: 8, color: '#4caf50', padding: '11px 28px', cursor: 'pointer', fontWeight: 'bold', fontSize: 14 },

  // Columna derecha
  colRight:      { display: 'flex', flexDirection: 'column', gap: 16 },
  plCard:        { background: '#1e1e2e', borderRadius: 12, padding: 24 },
  bancoCard:     { background: '#1e1e2e', borderRadius: 12, padding: 24 },
  plTitulo:      { color: '#fff', fontWeight: 'bold', fontSize: 14, marginBottom: 16 },
  plSeccion:     { color: '#555', fontSize: 10, fontWeight: 'bold', letterSpacing: 1, marginTop: 12, marginBottom: 8 },
  plRow:         { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' },
  plDivider:     { borderTop: '1px solid #2d2d44', margin: '8px 0' },
  bancoCuenta:   { background: '#0f0f1a', borderRadius: 8, padding: '12px 16px', marginBottom: 10 },
  bancoRow:      { display: 'flex', justifyContent: 'space-between', padding: '3px 0' },
  bancoLabel:    { color: '#555', fontSize: 12 },
  bancoVal:      { color: '#aaa', fontSize: 13 },
  diffCard:      { background: '#0f0f1a', borderRadius: 8, padding: '12px 16px', marginTop: 4, border: '1px solid #2d2d44' },
};